/**
 * Field Mapping Utility
 * Handles conversion between frontend camelCase and backend snake_case field names
 */

// Field mappings for different entities
const FIELD_MAPPINGS = {
  patient: {
    // Frontend (camelCase) -> Backend (snake_case)
    toBackend: {
      id: 'id', // Preserve ID field
      firstName: 'first_name',
      lastName: 'last_name',
      dateOfBirth: 'date_of_birth',
      dob: 'date_of_birth', // Support both dob and dateOfBirth
      asaScore: 'asa_score',
      asaClass: 'asa_class',
      surgeryDate: 'surgery_date',
      surgeryType: 'surgery_type',
      surgeonId: 'surgeon_id',
      surgeonName: 'surgeon_name',
      patientId: 'patient_id',
      medicalHistory: 'medical_history',
      facilityId: 'facility_id',
      height: 'height_cm', // Add mapping for height
      weight: 'weight_kg', // Add mapping for weight
      heightCm: 'height_cm',
      weightKg: 'weight_kg',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
      labs: 'labs', // Keep labs as is
      allergies: 'allergies', // Keep allergies as is
      medications: 'medications', // Keep medications as is
      comorbidities: 'medical_history', // Map comorbidities to medical_history
      emergency: 'emergency',
      urgency: 'urgency',
      mrn: 'mrn', // Preserve MRN field
    },
    // Backend (snake_case) -> Frontend (camelCase)
    toFrontend: {
      id: 'id', // Preserve ID field
      first_name: 'firstName',
      last_name: 'lastName',
      date_of_birth: 'dateOfBirth',
      asa_score: 'asaScore',
      asa_class: 'asaScore', // Map both to asaScore
      surgery_date: 'surgeryDate',
      surgery_type: 'surgeryType',
      surgeon_id: 'surgeonId',
      surgeon_name: 'surgeonName',
      patient_id: 'patientId',
      medical_history: 'medicalHistory',
      facility_id: 'facilityId',
      height_cm: 'heightCm',
      weight_kg: 'weightKg',
      created_at: 'createdAt',
      updated_at: 'updatedAt',
      scheduled_date: 'surgeryDate', // For surgeries table
      procedure_name: 'surgeryType',
      procedure_type: 'surgeryType',
      mrn: 'mrn', // Preserve MRN field
    },
  },
  surgery: {
    toBackend: {
      patientId: 'patient_id',
      surgeonId: 'surgeon_id',
      surgeryName: 'surgery_name',
      surgeryType: 'surgery_type',
      surgeryDate: 'surgery_date',
      scheduledDate: 'scheduled_date',
      procedureName: 'procedure_name',
      procedureType: 'procedure_type',
      facilityId: 'facility_id',
      roomId: 'room_id',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
    },
    toFrontend: {
      patient_id: 'patientId',
      surgeon_id: 'surgeonId',
      surgery_name: 'surgeryName',
      surgery_type: 'surgeryType',
      surgery_date: 'surgeryDate',
      scheduled_date: 'surgeryDate',
      procedure_name: 'surgeryName',
      procedure_type: 'surgeryType',
      facility_id: 'facilityId',
      room_id: 'roomId',
      created_at: 'createdAt',
      updated_at: 'updatedAt',
    },
  },
  order: {
    toBackend: {
      patientId: 'patient_id',
      surgeryId: 'surgery_id',
      orderType: 'order_type',
      orderName: 'order_name',
      orderDetails: 'order_details',
      orderedBy: 'ordered_by',
      orderedAt: 'ordered_at',
      completedAt: 'completed_at',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
    },
    toFrontend: {
      patient_id: 'patientId',
      surgery_id: 'surgeryId',
      order_type: 'orderType',
      order_name: 'orderName',
      order_details: 'orderDetails',
      ordered_by: 'orderedBy',
      ordered_at: 'orderedAt',
      completed_at: 'completedAt',
      created_at: 'createdAt',
      updated_at: 'updatedAt',
    },
  },
  surgeon: {
    toBackend: {
      facilityId: 'facility_id',
      licenseNumber: 'license_number',
      createdAt: 'created_at',
      updatedAt: 'updated_at',
    },
    toFrontend: {
      facility_id: 'facilityId',
      license_number: 'licenseNumber',
      created_at: 'createdAt',
      updated_at: 'updatedAt',
    },
  },
};

/**
 * Transform a single field name
 * @param {string} fieldName - The field name to transform
 * @param {string} entity - The entity type (patient, surgery, order, surgeon)
 * @param {string} direction - 'toBackend' or 'toFrontend'
 * @returns {string} The transformed field name
 */
export function transformFieldName(fieldName, entity, direction) {
  const mapping = FIELD_MAPPINGS[entity]?.[direction];
  return mapping?.[fieldName] || fieldName;
}

/**
 * Transform an object's field names
 * @param {Object} obj - The object to transform
 * @param {string} entity - The entity type (patient, surgery, order, surgeon)
 * @param {string} direction - 'toBackend' or 'toFrontend'
 * @returns {Object} New object with transformed field names
 */
export function transformFields(obj, entity, direction) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(item => transformFields(item, entity, direction));
  }

  const mapping = FIELD_MAPPINGS[entity]?.[direction] || {};
  const transformed = {};

  Object.keys(obj).forEach(key => {
    const newKey = mapping[key] || key;
    const value = obj[key];

    // Handle nested objects for specific fields
    if (key === 'surgery' && typeof value === 'object') {
      transformed[newKey] = transformFields(value, 'surgery', direction);
    } else if (key === 'surgeon' && typeof value === 'object') {
      transformed[newKey] = transformFields(value, 'surgeon', direction);
    } else if (key === 'orders' && Array.isArray(value)) {
      transformed[newKey] = value.map(order => transformFields(order, 'order', direction));
    } else {
      transformed[newKey] = value;
    }
  });

  return transformed;
}

/**
 * Transform patient data from backend format to frontend format
 * @param {Object|Array} data - Patient data from API
 * @returns {Object|Array} Transformed patient data
 */
export function transformPatientFromBackend(data) {
  return transformFields(data, 'patient', 'toFrontend');
}

/**
 * Transform patient data from frontend format to backend format
 * @param {Object} data - Patient data from frontend
 * @returns {Object} Transformed patient data for API
 */
export function transformPatientToBackend(data) {
  return transformFields(data, 'patient', 'toBackend');
}

/**
 * Transform surgery data from backend format to frontend format
 * @param {Object|Array} data - Surgery data from API
 * @returns {Object|Array} Transformed surgery data
 */
export function transformSurgeryFromBackend(data) {
  return transformFields(data, 'surgery', 'toFrontend');
}

/**
 * Transform order data from backend format to frontend format
 * @param {Object|Array} data - Order data from API
 * @returns {Object|Array} Transformed order data
 */
export function transformOrderFromBackend(data) {
  return transformFields(data, 'order', 'toFrontend');
}

/**
 * Transform surgeon data from backend format to frontend format
 * @param {Object|Array} data - Surgeon data from API
 * @returns {Object|Array} Transformed surgeon data
 */
export function transformSurgeonFromBackend(data) {
  return transformFields(data, 'surgeon', 'toFrontend');
}

// Utility to handle mixed responses that might contain multiple entity types
export function transformMixedResponse(response) {
  if (!response || typeof response !== 'object') return response;

  // If it's a standard API response with success/data structure
  if (response.success && response.data) {
    // Try to detect the entity type based on the data
    if (response.data.mrn || response.data.asaScore || response.data.firstName) {
      // Likely patient data
      return {
        ...response,
        data: transformPatientFromBackend(response.data),
      };
    } else if (response.data.surgeryType || response.data.procedureName) {
      // Likely surgery data
      return {
        ...response,
        data: transformSurgeryFromBackend(response.data),
      };
    } else if (response.data.orderType || response.data.orderName) {
      // Likely order data
      return {
        ...response,
        data: transformOrderFromBackend(response.data),
      };
    }
  }

  return response;
}
